import React, { useEffect, useState } from 'react';
import { useParams, Link as RouterLink } from 'react-router-dom';
import {
    Box, Container, Heading, Text, Button, Alert, AlertIcon,
    List, ListItem, ListIcon, Link, Skeleton, SkeletonText,
} from '@chakra-ui/react';
import { CheckCircleIcon, ArrowBackIcon } from '@chakra-ui/icons';
import axios from 'axios';
import FAQHead from '../Components/FAQ/FAQHead';
import TenantHeader from '../Components/FAQ/TenantHeader';

const ServiceLandingPage = () => {
    const { laundryId, serviceSlug } = useParams();
    const [page, setPage] = useState(null);
    const [loading, setLoading] = useState(true);
    const [notFound, setNotFound] = useState(false);

    useEffect(() => {
        axios.get(`${process.env.REACT_APP_AWS_API_URL}/api/city-pages/${laundryId}/service/${serviceSlug}`)
            .then((res) => setPage(res.data))
            .catch((err) => {
                if (err.response && err.response.status === 404) setNotFound(true);
            })
            .finally(() => setLoading(false));
    }, [laundryId, serviceSlug]);

    if (loading) {
        return (
            <Container maxW="900px" py={10}>
                <Skeleton height="40px" mb={4} />
                <SkeletonText noOfLines={6} spacing={4} />
            </Container>
        );
    }
    if (notFound || !page) {
        return (
            <Container maxW="900px" py={10}>
                <Alert status="warning" borderRadius="md" mb={6}>
                    <AlertIcon />
                    This service page was not found.
                </Alert>
                <Link as={RouterLink} to={`/${laundryId}/site`} color="blue.600">← Back to home</Link>
            </Container>
        );
    }

    return (
        <>
            <TenantHeader />
            <Container maxW="900px" py={10}>
                <FAQHead
                    title={page.pageTitle}
                    description={page.metaDescription}
                    keywords={page.keywords}
                    canonicalUrl={page.canonicalUrl || `/${laundryId}/service/${serviceSlug}`}
                    jsonLd={page.jsonLd}
                />
                <Heading as="h1" size="xl" mb={4} color="blue.700">{page.heroHeadline}</Heading>
                <Text fontSize="lg" color="gray.700" mb={6}>{page.bodyContent}</Text>
                <List spacing={3} mb={8}>
                    {page.price && (
                        <ListItem>
                            <ListIcon as={CheckCircleIcon} color="green.500" />
                            <strong>Price:</strong> {page.price}
                        </ListItem>
                    )}
                    {page.towns && page.towns.length > 0 && (
                        <ListItem>
                            <ListIcon as={CheckCircleIcon} color="green.500" />
                            Pickup towns: {page.towns.join(', ')}
                        </ListItem>
                    )}
                    {page.phone && (
                        <ListItem>
                            <ListIcon as={CheckCircleIcon} color="green.500" />
                            Call <Link href={`tel:${page.phone}`} color="blue.600">{page.phone}</Link>
                        </ListItem>
                    )}
                </List>
                <Box textAlign="center" mb={8}>
                    <Button as={RouterLink} to={`/${laundryId}/site`} colorScheme="blue" size="lg" borderRadius="full">
                        Schedule a Pickup
                    </Button>
                </Box>
                <Link as={RouterLink} to={`/${laundryId}/site`} color="gray.500" fontSize="sm">
                    <ArrowBackIcon mr={1} /> Back to {page.laundryName}
                </Link>
            </Container>
        </>
    );
};

export default ServiceLandingPage;
